import type { TapOutboxRepository, TapOutboxState } from "../domain/tapOutbox";

const DATABASE_NAME = "diamond-kingdom-tap-outbox";
const STORE_NAME = "outboxes";
const DATABASE_VERSION = 1;
const LOCAL_STORAGE_PREFIX = "diamond-kingdom:tap-outbox:";

type StoredOutbox = TapOutboxState & { scope: string };

function cloneState(state: TapOutboxState): TapOutboxState {
  return structuredClone(state);
}

function isSafeInteger(value: unknown, minimum = 0): value is number {
  return Number.isSafeInteger(value) && Number(value) >= minimum;
}

function isIntegerString(value: unknown): value is string {
  return typeof value === "string" && /^\d+$/.test(value);
}

function parseStoredOutbox(value: unknown, scope: string): TapOutboxState | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const record = value as Partial<StoredOutbox>;
  if (
    record.scope !== scope ||
    record.version !== 1 ||
    typeof record.ownerKey !== "string" ||
    typeof record.seasonId !== "string" ||
    typeof record.clientSessionId !== "string" ||
    !isIntegerString(record.lastAssignedSeq) ||
    !Array.isArray(record.batches)
  ) {
    return null;
  }
  const batches = record.batches.filter((batch): batch is TapOutboxState["batches"][number] => {
    if (typeof batch !== "object" || batch === null || Array.isArray(batch)) return false;
    const item = batch as Partial<TapOutboxState["batches"][number]>;
    return (
      typeof item.rootCommandId === "string" &&
      typeof item.commandId === "string" &&
      typeof item.clientSessionId === "string" &&
      isIntegerString(item.firstSeq) &&
      isIntegerString(item.lastSeq) &&
      isSafeInteger(item.requestedCount, 1) &&
      isSafeInteger(item.originalRequestedCount, 1) &&
      isSafeInteger(item.acceptedCount) &&
      isSafeInteger(item.energySpent) &&
      isIntegerString(item.optimisticRewardPerTap) &&
      isSafeInteger(item.rateRetryAttempt) &&
      isSafeInteger(item.deliveryAttempts) &&
      isSafeInteger(item.nextAttemptAt) &&
      isSafeInteger(item.createdAt)
    );
  });
  if (batches.length !== record.batches.length) return null;
  return cloneState({
    version: 1,
    ownerKey: record.ownerKey,
    seasonId: record.seasonId,
    clientSessionId: record.clientSessionId,
    lastAssignedSeq: record.lastAssignedSeq,
    batches,
  });
}

export class MemoryTapOutboxRepository implements TapOutboxRepository {
  readonly #states = new Map<string, TapOutboxState>();

  load(scope: string): Promise<TapOutboxState | null> {
    const state = this.#states.get(scope);
    return Promise.resolve(state === undefined ? null : cloneState(state));
  }

  save(scope: string, state: TapOutboxState): Promise<void> {
    this.#states.set(scope, cloneState(state));
    return Promise.resolve();
  }

  clear(scope: string): Promise<void> {
    this.#states.delete(scope);
    return Promise.resolve();
  }
}

export class LocalStorageTapOutboxRepository implements TapOutboxRepository {
  readonly #memory = new MemoryTapOutboxRepository();
  readonly #storage: Storage | null;
  readonly #memoryFallbackScopes = new Set<string>();

  constructor(storage?: Storage | null) {
    if (storage !== undefined) {
      this.#storage = storage;
      return;
    }
    try {
      this.#storage = typeof localStorage === "undefined" ? null : localStorage;
    } catch {
      this.#storage = null;
    }
  }

  async load(scope: string): Promise<TapOutboxState | null> {
    if (this.#storage === null) return this.#memory.load(scope);
    try {
      const raw = this.#storage.getItem(this.#key(scope));
      if (raw === null) {
        return this.#memoryFallbackScopes.has(scope) ? await this.#memory.load(scope) : null;
      }
      const parsed = parseStoredOutbox(JSON.parse(raw) as unknown, scope);
      if (parsed === null) {
        this.#storage.removeItem(this.#key(scope));
        return this.#memoryFallbackScopes.has(scope) ? await this.#memory.load(scope) : null;
      }
      this.#memoryFallbackScopes.delete(scope);
      await this.#memory.save(scope, parsed);
      return parsed;
    } catch {
      return this.#memory.load(scope);
    }
  }

  async save(scope: string, state: TapOutboxState): Promise<void> {
    await this.#memory.save(scope, state);
    if (this.#storage === null) return;
    try {
      this.#storage.setItem(this.#key(scope), JSON.stringify({ ...cloneState(state), scope } satisfies StoredOutbox));
      this.#memoryFallbackScopes.delete(scope);
    } catch {
      // The in-memory mirror still protects this page session.
      this.#memoryFallbackScopes.add(scope);
    }
  }

  async clear(scope: string): Promise<void> {
    this.#memoryFallbackScopes.delete(scope);
    await this.#memory.clear(scope);
    if (this.#storage === null) return;
    try {
      this.#storage.removeItem(this.#key(scope));
    } catch {
      // An unavailable browser store has no recoverable record to remove.
    }
  }

  #key(scope: string): string {
    return `${LOCAL_STORAGE_PREFIX}${encodeURIComponent(scope)}`;
  }
}

export class IndexedDbTapOutboxRepository implements TapOutboxRepository {
  readonly #fallback = new LocalStorageTapOutboxRepository();
  #database: Promise<IDBDatabase> | null = null;
  #indexedDbUnavailable = false;

  async load(scope: string): Promise<TapOutboxState | null> {
    const fallback = await this.#fallback.load(scope);
    // The mirror is written before IndexedDB. If a WebView is killed between
    // those writes, localStorage contains the newest sequence while IndexedDB
    // can still contain an older snapshot. Prefer the mirror whenever it is
    // present so a stale primary record can never hide newer pending taps.
    if (fallback !== null) return fallback;
    if (this.#indexedDbUnavailable) return fallback;
    try {
      const raw = await this.#read(scope);
      const parsed = parseStoredOutbox(raw, scope);
      if (raw === undefined) return fallback;
      if (parsed === null) {
        await this.#deleteFromIndexedDb(scope);
        return fallback;
      }
      return parsed;
    } catch {
      this.#indexedDbUnavailable = true;
      return fallback;
    }
  }

  async save(scope: string, state: TapOutboxState): Promise<void> {
    // Mirror the tiny outbox in both browser stores. Some Telegram WebViews
    // intermittently make only one of them available across a process reload.
    await this.#fallback.save(scope, state);
    if (this.#indexedDbUnavailable) return;
    try {
      const database = await this.#open();
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, "readwrite");
        transaction.objectStore(STORE_NAME).put({ ...cloneState(state), scope } satisfies StoredOutbox);
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error ?? new Error("Tap outbox write failed"));
        transaction.onabort = () => reject(transaction.error ?? new Error("Tap outbox write aborted"));
      });
    } catch {
      this.#indexedDbUnavailable = true;
    }
  }

  async clear(scope: string): Promise<void> {
    await this.#fallback.clear(scope);
    if (this.#indexedDbUnavailable) return;
    try {
      await this.#deleteFromIndexedDb(scope);
    } catch {
      this.#indexedDbUnavailable = true;
    }
  }

  async #deleteFromIndexedDb(scope: string): Promise<void> {
    const database = await this.#open();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).delete(scope);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Tap outbox delete failed"));
      transaction.onabort = () => reject(transaction.error ?? new Error("Tap outbox delete aborted"));
    });
  }

  async #read(scope: string): Promise<unknown> {
    const database = await this.#open();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readonly");
      const request = transaction.objectStore(STORE_NAME).get(scope);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Tap outbox read failed"));
    });
  }

  #open(): Promise<IDBDatabase> {
    if (this.#database !== null) return this.#database;
    if (typeof indexedDB === "undefined") return Promise.reject(new Error("IndexedDB is unavailable"));
    this.#database = new Promise((resolve, reject) => {
      const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE_NAME)) {
          request.result.createObjectStore(STORE_NAME, { keyPath: "scope" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Tap outbox open failed"));
      request.onblocked = () => reject(new Error("Tap outbox open blocked"));
    });
    return this.#database;
  }
}
